import { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppContext } from "../context/app/context";
import { LOCAL_STORAGE_KEYS } from "../common/constants";

interface ProtectedRouteProps {
  children: ReactElement;
  path: string;
}

export function ProtectedRoute({ children, path }: ProtectedRouteProps) {
  const { state } = useAppContext();
  const location = useLocation();
  const user = state.user;

  // Extract store ID from URL pathname (more reliable than params) - do this before early returns
  const pathMatch = location.pathname.match(/^\/operation-panel\/(\d+)/);
  const storeIdFromUrl = pathMatch ? parseInt(pathMatch[1], 10) : null;

  // Get user data (safe even if user is null)
  const permissionNames = user?.permissionNames || [];
  // Try multiple variations of the permission name for robustness
  const hasStoreAdminPermission = permissionNames.some((name: string) => 
    name?.toLowerCase().includes("store admin") || 
    name?.toLowerCase() === "store admin" ||
    name === "store admin"
  );
  const hasSuperPartner = permissionNames.some((name: string) => 
    name?.toLowerCase().includes("super partner") || 
    name?.toLowerCase() === "super partner" ||
    name === "Super Partner"
  );
  const isStoreAdmin = hasStoreAdminPermission && !hasSuperPartner;
  const isSuperPartner = hasSuperPartner;
  const isOperationPanelPath = path.startsWith("/operation-panel") || location.pathname.startsWith("/operation-panel");
  const userStoreId = user?.storeId;
  
  // Normalize userStoreId to number for comparison
  const userStoreIdNum = userStoreId ? (typeof userStoreId === 'number' ? userStoreId : parseInt(String(userStoreId), 10)) : null;

  // Check if this is a public route (invite, login, etc.) - allow access without authentication
  const isPublicRoute = location.pathname.startsWith("/invite/") || 
                        location.pathname.startsWith("/login") ||
                        location.pathname.startsWith("/forgot-password") ||
                        location.pathname.startsWith("/reset-password/");

  // Allow public routes to be accessed even without authentication or when logged in
  if (isPublicRoute) {
    return children;
  }

  // Early return after hooks - require authentication for protected routes
  if (!user) {
    const sessionToken = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    // Avoid bouncing between /login and protected routes while auth state is hydrating.
    if (sessionToken && refreshToken) {
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // Store admin can only access operation panel routes
  // But allow public routes (already handled above)
  // Redirect immediately if they're on a non-operation-panel route
  if (isStoreAdmin && !isOperationPanelPath && !isPublicRoute && user) {
    // Redirect to operation panel with their storeId
    if (userStoreIdNum) {
      return <Navigate to={`/operation-panel/${userStoreIdNum}`} replace />;
    } else {
      // No storeId, redirect to operation panel list
      return <Navigate to="/operation-panel" replace />;
    }
  }

  // If store admin is accessing operation panel, validate store ID immediately
  // This check runs synchronously before rendering to prevent unauthorized access
  if (isStoreAdmin && isOperationPanelPath) {
    if (userStoreIdNum && storeIdFromUrl) {
      // Both IDs exist, compare them
      if (!isNaN(storeIdFromUrl) && !isNaN(userStoreIdNum) && storeIdFromUrl !== userStoreIdNum) {
        // Redirect to their own store ID immediately
        return <Navigate to={`/operation-panel/${userStoreIdNum}`} replace />;
      }
    } else if (userStoreIdNum && !storeIdFromUrl) {
      // User has storeId but URL doesn't have one, redirect to their store
      return <Navigate to={`/operation-panel/${userStoreIdNum}`} replace />;
    }
  }

  // Super Partner can access everything
  if (isSuperPartner) {
    return children;
  }

  // For other users, allow access (they should have been filtered by useAuthenticate)
  return children;
}
